import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Megaphone, FolderKanban, Palette, 
  DollarSign, Sparkles, ListTodo, Settings, Menu, X, ChevronLeft,
  BarChart3, FileText, LogOut, MessageCircle, Bot, Target, Cpu, Crown, Inbox, Search
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/AuthContext';
import { Logo, LogoIcon } from './Logo';

const adminLinks = [
  { to: '/admin', icon: LayoutDashboard, label: 'Überblick' },
  { to: '/admin/kompetenz', icon: Target, label: 'Kompetenz-Hub' },
  { to: '/admin/clients', icon: Users, label: 'Kunden (CRM)' },
  { to: '/admin/leads', icon: Inbox, label: 'Leads' },
  { to: '/admin/seo', icon: Search, label: 'SEO' },
  { to: '/admin/audit', icon: Cpu, label: 'Audit & Tracking' },
  { to: '/admin/campaigns', icon: Megaphone, label: 'Kampagnen' },
  { to: '/admin/projects', icon: FolderKanban, label: 'Projekte' },
  { to: '/admin/creatives', icon: Palette, label: 'Creatives' },
  { to: '/admin/finance', icon: DollarSign, label: 'Finanzen' },
  { to: '/admin/ai-tools', icon: Sparkles, label: 'AI-Tools' },
  { to: '/admin/ai-chat', icon: Bot, label: 'marKI Chat' },
  { to: '/admin/corporate', icon: Crown, label: 'Unternehmensentw.', premium: true },
  { to: '/admin/tasks', icon: ListTodo, label: 'Tasks' },
  { to: '/admin/settings', icon: Settings, label: 'Einstellungen' },
];

const clientLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Übersicht' },
  { to: '/dashboard/campaigns', icon: Megaphone, label: 'Kampagnen' },
  { to: '/dashboard/reports', icon: FileText, label: 'Reports' },
];

export const AppShell = ({ mode = 'admin' }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    document.title = mode === 'admin' ? 'Admin | marKIpilot' : 'Dashboard | marKIpilot';
  }, [mode]);
  const links = mode === 'admin' ? adminLinks : clientLinks;

  const isActive = (path) => {
    if (path === '/admin' || path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-slate-200" style={mobile ? { paddingTop: 'calc(1rem + env(safe-area-inset-top))' } : {}}>
        <Link to="/">
          {collapsed && !mobile ? <LogoIcon /> : <Logo size="default" />}
        </Link>
      </div>
      
      <nav className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        {links.map((link) => {
          const Icon = link.icon;
          const active = isActive(link.to);
          return (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`sidebar-nav-${link.to.split('/').pop() || 'overview'}-link`}
              className={cn(
                'sidebar-link flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium relative',
                active 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                link.premium && !active && 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-800 hover:from-amber-100 hover:to-orange-100'
              )}
            >
              <Icon className={cn("w-5 h-5 flex-shrink-0", link.premium && !active && "text-amber-600")} />
              {(!collapsed || mobile) && <span className="truncate">{link.label}</span>}
              {link.premium && (!collapsed || mobile) && (
                <span className="ml-auto text-[9px] font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">PRO</span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-200" style={mobile ? { paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' } : {}}>
        {user && (!collapsed || mobile) && (
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
            <p className="text-xs text-slate-400 truncate">{user.email}</p>
            <span className="inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{user.role}</span>
          </div>
        )}
        <button
          onClick={() => { logout(); navigate('/login'); }}
          data-testid="sidebar-nav-logout-button"
          className="sidebar-link flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full"
        >
          <LogOut className="w-5 h-5" />
          {(!collapsed || mobile) && <span>Abmelden</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#F6F8FB]" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      {/* Desktop Sidebar */}
      <aside className={cn(
        'hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all duration-200',
        collapsed ? 'w-16' : 'w-60'
      )}>
        <SidebarContent />
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center p-2 border-t border-slate-200 text-slate-400 hover:text-slate-600"
        >
          <ChevronLeft className={cn('w-4 h-4 transition-transform', collapsed && 'rotate-180')} />
        </button>
      </aside>

      {/* Mobile Sidebar */}
      <Sheet>
        <div className="flex-1 flex flex-col min-w-0">
          {/* Topbar */}
          <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
            <div className="flex items-center gap-3">
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="sm" data-testid="mobile-menu-toggle">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <h1 className="font-display font-semibold text-sm text-slate-900 truncate">
                {mode === 'admin' ? 'Admin Panel' : 'Client Dashboard'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {user && (
                <span className="text-xs text-slate-500 truncate max-w-[120px] hidden sm:block">{user.name}</span>
              )}
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-auto p-4 lg:p-6" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
            <Outlet />
          </main>
        </div>

        <SheetContent side="left" className="p-0 w-[280px] bg-white">
          <SheetTitle className="sr-only">Navigationsmenü</SheetTitle>
          <SidebarContent mobile />
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AppShell;
